import { Box, Button, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
	ConsentOpenEvent,
	ConsentSettingsHash,
	readConsent,
	saveConsent,
} from '../../helpers/consent';
import { path, routes } from '../../routes';

// Texts live here instead of in the locale files so the banner works on every
// site without a translation run. Dutch is the fallback.
const texts = {
	nl: {
		title: 'Cookies',
		body: 'We gebruiken cookies om de website goed te laten werken, om te meten hoe de site wordt gebruikt en om je relevante advertenties te tonen. Kies zelf wat je toestaat; je kunt dit altijd aanpassen via "Cookie-instellingen" onderaan de pagina.',
		more: 'Lees de cookieverklaring',
		accept: 'Alles accepteren',
		reject: 'Alleen noodzakelijk',
		settings: 'Voorkeuren',
		save: 'Voorkeuren opslaan',
		necessary: 'Noodzakelijk (altijd aan): inloggen, winkelmandje en betalen.',
		analytics: 'Statistieken: meten hoe de website wordt gebruikt (Google Analytics).',
		marketing: 'Marketing: advertenties meten en persoonlijker maken (Google Ads, Meta).',
	},
	en: {
		title: 'Cookies',
		body: 'We use cookies to make the website work, to measure how it is used and to show you relevant ads. Choose what you allow; you can change this any time via "Cookie settings" at the bottom of the page.',
		more: 'Read the cookie statement',
		accept: 'Accept all',
		reject: 'Necessary only',
		settings: 'Preferences',
		save: 'Save preferences',
		necessary: 'Necessary (always on): logging in, basket and payment.',
		analytics: 'Statistics: measuring how the website is used (Google Analytics).',
		marketing: 'Marketing: measuring and personalising ads (Google Ads, Meta).',
	},
	de: {
		title: 'Cookies',
		body: 'Wir verwenden Cookies, damit die Website funktioniert, um die Nutzung zu messen und um dir relevante Werbung zu zeigen. Du entscheidest, was du erlaubst; du kannst es jederzeit über "Cookie-Einstellungen" unten auf der Seite ändern.',
		more: 'Cookie-Erklärung lesen',
		accept: 'Alle akzeptieren',
		reject: 'Nur notwendige',
		settings: 'Einstellungen',
		save: 'Einstellungen speichern',
		necessary: 'Notwendig (immer aktiv): Anmelden, Warenkorb und Bezahlen.',
		analytics: 'Statistik: Messen, wie die Website genutzt wird (Google Analytics).',
		marketing: 'Marketing: Werbung messen und personalisieren (Google Ads, Meta).',
	},
};

export function getCookieTexts(language){
	return texts[(language || 'nl').substring(0, 2)] || texts.nl;
}

const StyledBanner = styled(Paper)(({ theme }) => ({
	position: 'fixed',
	left: theme.spacing(2),
	right: theme.spacing(2),
	bottom: theme.spacing(2),
	maxWidth: 640,
	margin: '0 auto',
	padding: theme.spacing(2.5),
	zIndex: 1400,
	borderRadius: 12,
}));

const StyledButtons = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	gap: theme.spacing(1),
	marginTop: theme.spacing(2),
}));

function CookieBanner(){
	const { i18n } = useTranslation();
	const text = getCookieTexts(i18n.language);

	// Only decide on the client, so server and client render the same markup.
	const [open, setOpen] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	// Pre-ticked boxes don't count as consent under the AVG/GDPR, so both start off.
	const [analytics, setAnalytics] = useState(false);
	const [marketing, setMarketing] = useState(false);

	const openSettings = useCallback(() => {
		const stored = readConsent();

		if(stored){
			setAnalytics(stored.analytics);
			setMarketing(stored.marketing);
		}

		setShowSettings(true);
		setOpen(true);
	}, []);

	useEffect(() => {
		if(!readConsent()) setOpen(true);

		const onHash = () => {
			if(window.location.hash === ConsentSettingsHash){
				openSettings();
				window.history.replaceState(null, '', window.location.pathname + window.location.search);
			}
		};

		onHash();
		window.addEventListener(ConsentOpenEvent, openSettings);
		window.addEventListener('hashchange', onHash);

		return () => {
			window.removeEventListener(ConsentOpenEvent, openSettings);
			window.removeEventListener('hashchange', onHash);
		};
	}, [openSettings]);

	const choose = useCallback((choice) => {
		saveConsent(choice);
		setOpen(false);
		setShowSettings(false);
	}, []);

	if(!open) return null;

	return (
		<StyledBanner elevation={8} role="dialog" aria-live="polite" aria-label={text.title}>
			<Typography variant="h6" component="p" sx={{ mb: 1 }}>{text.title}</Typography>
			<Typography variant="body2">
				{text.body}{' '}
				<Link to={path(i18n, routes.STATIC.COOKIES)}>{text.more}</Link>
			</Typography>

			{showSettings && (
				<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
					<FormControlLabel control={<Switch checked disabled />} label={<Typography variant="body2">{text.necessary}</Typography>} />
					<FormControlLabel control={<Switch checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />} label={<Typography variant="body2">{text.analytics}</Typography>} />
					<FormControlLabel control={<Switch checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />} label={<Typography variant="body2">{text.marketing}</Typography>} />
				</Box>
			)}

			{/* Accept and reject are equally prominent, as the Dutch regulator requires. */}
			<StyledButtons>
				<Button variant="contained" onClick={() => choose({ analytics: true, marketing: true })}>{text.accept}</Button>
				<Button variant="contained" onClick={() => choose({ analytics: false, marketing: false })}>{text.reject}</Button>
				{showSettings ? (
					<Button variant="outlined" onClick={() => choose({ analytics, marketing })}>{text.save}</Button>
				) : (
					<Button variant="text" onClick={() => setShowSettings(true)}>{text.settings}</Button>
				)}
			</StyledButtons>
		</StyledBanner>
	);
}

export default CookieBanner;
